import { createContext, useContext, useState, useEffect, useCallback } from "react";
import localforage from "localforage";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure localforage
localforage.config({
  name: "AquaReport",
  storeName: "offline_data",
});

const OfflineContext = createContext(null);

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error("useOffline must be used within an OfflineProvider");
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);
  const { token, user } = useAuth();

  // Monitor online status
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("Connexion rétablie");
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("Mode hors ligne activé");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Check pending items count
  const checkPendingSync = useCallback(async () => {
    try {
      const pendingClaims = (await localforage.getItem("pending_claims")) || [];
      const pendingOrders = (await localforage.getItem("pending_orders")) || [];
      setPendingSync(pendingClaims.length + pendingOrders.length);
    } catch (error) {
      console.error("Error checking pending sync:", error);
    }
  }, []);

  useEffect(() => {
    checkPendingSync();
  }, [checkPendingSync]);

  // Save claim locally
  const saveClaimLocally = async (claim) => {
    try {
      const pendingClaims = (await localforage.getItem("pending_claims")) || [];
      const existingIndex = pendingClaims.findIndex((c) => c.id === claim.id);
      
      if (existingIndex >= 0) {
        pendingClaims[existingIndex] = claim;
      } else {
        pendingClaims.push(claim);
      }
      
      await localforage.setItem("pending_claims", pendingClaims);
      await checkPendingSync();
      
      return claim;
    } catch (error) {
      console.error("Error saving claim locally:", error);
      throw error;
    }
  };

  // Save work order locally
  const saveWorkOrderLocally = async (order) => {
    try {
      const pendingOrders = (await localforage.getItem("pending_orders")) || [];
      const existingIndex = pendingOrders.findIndex((o) => o.id === order.id);
      
      if (existingIndex >= 0) {
        pendingOrders[existingIndex] = order;
      } else {
        pendingOrders.push(order);
      }
      
      await localforage.setItem("pending_orders", pendingOrders);
      await checkPendingSync();
      
      return order;
    } catch (error) {
      console.error("Error saving work order locally:", error);
      throw error;
    }
  };

  // Get local claims
  const getLocalClaims = async () => {
    try {
      return (await localforage.getItem("pending_claims")) || [];
    } catch (error) {
      console.error("Error getting local claims:", error);
      return [];
    }
  };

  // Get local work orders
  const getLocalWorkOrders = async () => {
    try {
      return (await localforage.getItem("pending_orders")) || [];
    } catch (error) {
      console.error("Error getting local work orders:", error);
      return [];
    }
  };

  // Sync with server
  const syncWithServer = async () => {
    if (!isOnline || !token || isSyncing) return;

    setIsSyncing(true);
    try {
      const pendingClaims = await getLocalClaims();
      const pendingOrders = await getLocalWorkOrders();

      if (pendingClaims.length === 0 && pendingOrders.length === 0) {
        setIsSyncing(false);
        return;
      }

      const response = await axios.post(
        `${API}/sync`,
        {
          claim_files: pendingClaims,
          work_orders: pendingOrders,
          last_sync: await localforage.getItem("last_sync"),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Clear synced items
      await localforage.setItem("pending_claims", []);
      await localforage.setItem("pending_orders", []);
      await localforage.setItem("last_sync", response.data.sync_timestamp);

      // Cache server data
      await localforage.setItem("cached_claims", response.data.server_claims);
      await localforage.setItem("cached_orders", response.data.server_orders);

      await checkPendingSync();
      toast.success("Synchronisation terminée");
    } catch (error) {
      console.error("Sync error:", error);
      toast.error("Erreur de synchronisation");
    } finally {
      setIsSyncing(false);
    }
  };

  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline && token && pendingSync > 0) {
      syncWithServer();
    }
  }, [isOnline, token]);

  // Save photo locally (base64)
  const savePhotoLocally = async (claimId, photoBase64) => {
    try {
      const photos = (await localforage.getItem(`photos_${claimId}`)) || [];
      photos.push(photoBase64);
      await localforage.setItem(`photos_${claimId}`, photos);
      return photos;
    } catch (error) {
      console.error("Error saving photo locally:", error);
      throw error;
    }
  };

  // Get local photos
  const getLocalPhotos = async (claimId) => {
    try {
      return (await localforage.getItem(`photos_${claimId}`)) || [];
    } catch (error) {
      console.error("Error getting local photos:", error);
      return [];
    }
  };

  const value = {
    isOnline,
    isSyncing,
    pendingSync,
    saveClaimLocally,
    saveWorkOrderLocally,
    getLocalClaims,
    getLocalWorkOrders,
    syncWithServer,
    savePhotoLocally,
    getLocalPhotos,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};

export default OfflineContext;
